import { ArrowRight, Wallet, Split, Receipt, Landmark, RefreshCw } from 'lucide-react';

/**
 * Small "How money flows" explainer.
 *
 * Reusable across Dashboard, Account Balances, and Financial Plan Setup.
 * Visual: 5 connected step chips (Income → Plan → Expenses → Balances →
 * Rebalance) with a concise three-line narrative beneath. Fully static —
 * no animations, no calculations — matches the dark theme.
 *
 * Usage:
 *   <MoneyFlowExplainer />
 *   <MoneyFlowExplainer compact /> // skips the optional supporting bullets
 */
const STEPS = [
  { icon: Wallet,    label: 'Income',        tone: 'blue'    },
  { icon: Split,     label: 'Allocation Plan', tone: 'purple' },
  { icon: Receipt,   label: 'Expenses',      tone: 'rose'    },
  { icon: Landmark,  label: 'Current Balances', tone: 'emerald' },
  { icon: RefreshCw, label: 'Rebalance',     tone: 'amber'   },
];

const TONE_CLASSES = {
  blue:    'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300',
  purple:  'bg-purple-500/10 border-purple-500/30 text-purple-700 dark:text-purple-300',
  rose:    'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300',
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
  amber:   'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
};

function MoneyFlowExplainer({ compact = false, testId = 'money-flow-explainer' }) {
  return (
    <section
      data-testid={testId}
      className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg px-5 py-4"
      aria-label="How your money flows"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
        💡 How Your Money Flows
      </h3>

      {/* Step chips */}
      <ol
        className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-4"
        role="list"
      >
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <li key={step.label} className="flex items-center gap-2">
              <div
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${TONE_CLASSES[step.tone]}`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                <span>{step.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <ArrowRight
                  className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>

      {/* Narrative */}
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        <span className="block">Income sets the plan.</span>
        <span className="block">Spending changes the outcome.</span>
        <span className="block">Rebalancing brings everything back into alignment.</span>
      </p>

      {/* Optional supporting bullets */}
      {!compact && (
        <ul className="mt-3 space-y-1 text-xs text-gray-600 dark:text-gray-400">
          <li>• <strong>YTD Allocation</strong> shows how income was split.</li>
          <li>• <strong>Account Balances</strong> show what remains after expenses.</li>
          <li>• <strong>Rebalancing</strong> fixes the gap between expected and actual balances.</li>
        </ul>
      )}
    </section>
  );
}

export default MoneyFlowExplainer;
